/**
 * OkChatWidget — Floating chat button + OkChat modal controller.
 *
 * Renders a fixed MessageSquare button for logged-in users when OkChat is enabled.
 * Clicking it opens the OkChatModal (never navigates away).
 * window.okChatOpen({ sellerId?, sellerNickname?, adTitle? }) opens it programmatically.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { MessageSquare } from 'lucide-react';
import OkChatModal, { getCachedToken, setCachedToken, clearCachedToken } from './OkChatModal';

const ACCENT = '#0288D1';
const BADGE_BG = '#ef4444';
const POLL_INTERVAL_MS = 60 * 1000; // 60 seconds

export default function OkChatWidget() {
    const { user, role } = useAuth();
    const [enabled, setEnabled]   = useState(false);
    const [open, setOpen]         = useState(false);
    const [initSeller, setInitSeller] = useState({ id: null, nickname: null });
    const [unreadCount, setUnreadCount] = useState(0);

    // Stable user identifier — used to scope the OkChat token cache
    const userId = user?._id || user?.id || user?.nickname || null;
    // Track previous userId so we can detect account switches
    const prevUserIdRef = useRef(userId);

    // Check if OkChat is enabled
    useEffect(() => {
        if (role !== 'user') return;
        api.get('/okchat/status')
            .then(r => setEnabled(!!r.data?.enabled))
            .catch(() => setEnabled(false));
    }, [role]);

    // Clear cached token on logout or account switch so another user's tab
    // never inherits an OkChat session that doesn't belong to them.
    useEffect(() => {
        const prev = prevUserIdRef.current;
        prevUserIdRef.current = userId;

        if (prev && prev !== userId) {
            // User logged out or switched accounts — clear their token
            clearCachedToken(prev);
            setOpen(false);
            setUnreadCount(0);
        }
    }, [userId]);

    // Pre-fetch OkChat token for the current user so the first modal open is instant.
    // Uses a cancelled flag so a response arriving after logout/account-switch
    // cannot write into the cache for the wrong user.
    useEffect(() => {
        if (role !== 'user' || !userId) return;
        if (getCachedToken(userId)) return; // already cached and not expired

        let cancelled = false;
        api.get('/okchat/auth')
            .then(r => {
                if (!cancelled && r.data?.token) {
                    setCachedToken(userId, r.data.token, r.data.okchat_base_url || '');
                }
            })
            .catch(() => {}); // silent — cache miss; modal will auth on open
        return () => { cancelled = true; };
    }, [role, userId]);

    // Background unread-count polling — uses cached token, runs every 60 s.
    // Stops polling while the modal is open (user is actively reading).
    // If the cached token has expired (or was never set), silently re-authenticates
    // via /okchat/auth before retrying so the badge stays accurate across long sessions.
    const fetchUnread = useCallback(async () => {
        if (!userId || !enabled) return;
        let cached = getCachedToken(userId);
        if (!cached) {
            // Token missing or expired — attempt a silent refresh
            try {
                const authRes = await api.get('/okchat/auth');
                if (authRes.data?.token) {
                    setCachedToken(userId, authRes.data.token, authRes.data.okchat_base_url || '');
                    cached = getCachedToken(userId);
                }
            } catch {
                return; // auth failed — skip this poll cycle silently
            }
        }
        if (!cached) return; // still no token after refresh attempt
        try {
            const r = await api.get('/okchat/conversations', {
                params: { okchat_token: cached.token },
            });
            const list = r.data?.conversations || [];
            const total = list.reduce((s, c) => s + (c.unread_count || 0), 0);
            setUnreadCount(total);
        } catch {
            // silent — badge just stays at last known value
        }
    }, [userId, enabled]);

    useEffect(() => {
        if (open || !enabled || role !== 'user') return; // don't poll while modal is open
        fetchUnread(); // immediate first fetch
        const id = setInterval(fetchUnread, POLL_INTERVAL_MS);
        return () => clearInterval(id);
    }, [open, enabled, role, fetchUnread]);

    // Global opener — for "chat with seller" buttons across the app
    useEffect(() => {
        window.okChatOpen = (opts = {}) => {
            setInitSeller({ id: opts.sellerId || null, nickname: opts.sellerNickname || null });
            setOpen(true);
        };
        return () => { delete window.okChatOpen; };
    }, []);

    const handleOpen = () => {
        setInitSeller({ id: null, nickname: null });
        setOpen(true);
    };

    // Called by OkChatModal each time a conversation is opened and marked read.
    // Decrements the floating button badge by that conversation's unread count.
    const handleConvRead = useCallback((count) => {
        setUnreadCount(prev => Math.max(0, prev - (count || 0)));
    }, []);

    if (role !== 'user' || !enabled) return null;

    return (
        <>
            {/* Floating button */}
            {!open && (
                <button
                    onClick={handleOpen}
                    title="Abrir OkChat — Mensajería"
                    aria-label={unreadCount > 0 ? `Abrir chat — ${unreadCount} mensajes sin leer` : 'Abrir chat'}
                    style={{
                        position: 'fixed', bottom: 24, right: 96, zIndex: 8999,
                        width: 56, height: 56, borderRadius: '50%',
                        background: ACCENT, color: '#fff', border: 'none',
                        boxShadow: '0 4px 24px rgba(2,136,209,0.45)',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'transform 0.15s, box-shadow 0.15s',
                    }}
                    onMouseEnter={e => {
                        e.currentTarget.style.transform = 'scale(1.1)';
                        e.currentTarget.style.boxShadow = '0 6px 32px rgba(2,136,209,0.55)';
                    }}
                    onMouseLeave={e => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = '0 4px 24px rgba(2,136,209,0.45)';
                    }}
                >
                    <MessageSquare size={24} />
                    {unreadCount > 0 && (
                        <span
                            aria-hidden="true"
                            style={{
                                position: 'absolute',
                                top: 2, right: 2,
                                minWidth: 18, height: 18,
                                background: BADGE_BG,
                                color: '#fff',
                                borderRadius: 9,
                                fontSize: 10,
                                fontWeight: 700,
                                lineHeight: '18px',
                                textAlign: 'center',
                                padding: '0 4px',
                                boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                                pointerEvents: 'none',
                                boxSizing: 'border-box',
                            }}
                        >
                            {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                    )}
                </button>
            )}

            {/* OkChat modal — key={userId} forces a full remount on account switch,
                ensuring no prior user's token or conversation state leaks through. */}
            <OkChatModal
                key={userId || 'guest'}
                open={open}
                onClose={() => setOpen(false)}
                initialSellerId={initSeller.id}
                initialSellerNickname={initSeller.nickname}
                onConvRead={handleConvRead}
            />
        </>
    );
}
